import { useState, useMemo } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  Area,
  ComposedChart,
  ReferenceLine,
  Bar,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import SimulationParams from './simulation-parameters'

// Build a recovery function from roasData
const buildRecoveryFunction = (roasData) => {
  if (!roasData || roasData.length === 0) {
    return () => 0
  }

  const roasMap = new Map()
  roasData.forEach((item) => {
    roasMap.set(item.day, item.roas / 100)
  })

  const maxDay = Math.max(...roasData.map((d) => d.day))
  const maxRoas = roasMap.get(maxDay) || 1

  return (day) => {
    if (roasMap.has(day)) {
      return roasMap.get(day)
    }

    if (day > maxDay) {
      return maxRoas
    }

    const sortedDays = [...roasMap.keys()].sort((a, b) => a - b)
    let lowerDay = 0
    let upperDay = sortedDays[sortedDays.length - 1]

    for (let i = 0; i < sortedDays.length; i++) {
      if (sortedDays[i] <= day) {
        lowerDay = sortedDays[i]
      }
      if (sortedDays[i] >= day && upperDay > day) {
        upperDay = sortedDays[i]
        break
      }
    }

    const lowerRoas = roasMap.get(lowerDay) || 0
    const upperRoas = roasMap.get(upperDay) || 0

    if (upperDay === lowerDay) return lowerRoas

    const ratio = (day - lowerDay) / (upperDay - lowerDay)
    return lowerRoas + (upperRoas - lowerRoas) * ratio
  }
}

const FixedExpansion = ({ roasData }) => {
  const [initialFund, setInitialFund] = useState(100000)
  const [dailyCost, setDailyCost] = useState(1000)
  const [targetRoi, setTargetRoi] = useState(100)
  const [simulationDays, setSimulationDays] = useState(365)

  const getRecoveryRate = useMemo(
    () => buildRecoveryFunction(roasData),
    [roasData],
  )

  // Fixed expansion simulation
  // Spend a fixed daily amount regardless of fund pool status
  // Revenue accumulates and can be withdrawn or tracked separately
  const simulationData = useMemo(() => {
    const result = []
    let totalCost = 0
    let totalRevenue = 0
    let withdrawable = 0 // Revenue that exceeds the fixed daily cost

    // Track pending recoveries
    const pendingRecoveries = []

    for (let day = 1; day <= simulationDays; day++) {
      // Calculate today's revenue from all pending recoveries
      let todayRevenue = 0
      pendingRecoveries.forEach((recovery) => {
        const daysElapsed = day - recovery.daySpent
        const currentRecoveryRate = getRecoveryRate(daysElapsed)
        const prevRecoveryRate = getRecoveryRate(daysElapsed - 1)
        const dailyRecovery =
          (currentRecoveryRate - prevRecoveryRate) * recovery.amount
        todayRevenue += Math.max(0, dailyRecovery)
      })

      totalRevenue += todayRevenue

      // Fixed daily cost (always spend this amount)
      const todayCost = dailyCost
      totalCost += todayCost

      // Add to pending recoveries
      pendingRecoveries.push({
        daySpent: day,
        amount: todayCost,
      })

      // Calculate withdrawable (revenue that can be taken out)
      // This is total revenue minus initial investment used
      const usedFromInitial = Math.min(totalCost, initialFund)
      withdrawable = Math.max(0, totalRevenue - usedFromInitial)

      // Calculate metrics
      const roi = totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0
      const profit = totalRevenue - totalCost
      const netCashFlow = todayRevenue - todayCost
      const cumulativeNetCashFlow = totalRevenue - totalCost

      result.push({
        day,
        todayCost: Math.round(todayCost),
        todayRevenue: Math.round(todayRevenue),
        totalCost: Math.round(totalCost),
        totalRevenue: Math.round(totalRevenue),
        profit: Math.round(profit),
        withdrawable: Math.round(withdrawable),
        netCashFlow: Math.round(netCashFlow),
        cumulativeNetCashFlow: Math.round(cumulativeNetCashFlow),
        roi: roi.toFixed(2),
        hitTarget: roi >= targetRoi,
      })
    }

    return result
  }, [initialFund, dailyCost, targetRoi, simulationDays, getRecoveryRate])

  // Find breakeven day (when cumulative net cash flow becomes positive)
  const breakevenDay = useMemo(() => {
    const day = simulationData.find((d) => d.cumulativeNetCashFlow >= 0)
    return day?.day || null
  }, [simulationData])

  // Summary metrics
  const summary = useMemo(() => {
    const lastDay = simulationData[simulationData.length - 1]
    const totalInvestmentNeeded = dailyCost * simulationDays
    const remainingFromInitial = Math.max(
      0,
      initialFund - totalInvestmentNeeded,
    )

    return {
      totalCost: lastDay?.totalCost || 0,
      totalRevenue: lastDay?.totalRevenue || 0,
      profit: lastDay?.profit || 0,
      withdrawable: lastDay?.withdrawable || 0,
      finalRoi: lastDay?.roi || 0,
      breakevenDay,
      totalInvestmentNeeded,
      remainingFromInitial,
      additionalFundNeeded: Math.max(0, totalInvestmentNeeded - initialFund),
    }
  }, [simulationData, breakevenDay, dailyCost, simulationDays, initialFund])

  // Key milestones
  const milestones = useMemo(() => {
    const days = [30, 60, 90, 120, 180, 365]
    return days
      .filter((d) => d <= simulationDays)
      .map((d) => simulationData[d - 1])
      .filter(Boolean)
  }, [simulationData, simulationDays])

  if (!roasData || roasData.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
        No recovery model data available for simulation.
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      <SimulationParams
        initialFund={initialFund}
        setInitialFund={setInitialFund}
        dailyCost={dailyCost}
        setDailyCost={setDailyCost}
        targetRoi={targetRoi}
        setTargetRoi={setTargetRoi}
        simulationDays={simulationDays}
        setSimulationDays={setSimulationDays}
        title="Fixed Expansion Parameters"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Total Cost</div>
            <div className="text-2xl font-bold text-red-500">
              ${summary.totalCost.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Total Revenue</div>
            <div className="text-2xl font-bold text-green-600">
              ${summary.totalRevenue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Profit</div>
            <div
              className={`text-2xl font-bold ${
                summary.profit >= 0 ? 'text-green-600' : 'text-red-500'
              }`}
            >
              ${summary.profit.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Final ROI</div>
            <div
              className={`text-2xl font-bold ${
                Number(summary.finalRoi) >= targetRoi
                  ? 'text-green-600'
                  : 'text-amber-600'
              }`}
            >
              {summary.finalRoi}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Withdrawable</div>
            <div className="text-2xl font-bold text-purple-600">
              ${summary.withdrawable.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Breakeven Day</div>
            <div className="text-2xl font-bold text-blue-600">
              {summary.breakevenDay || 'N/A'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investment Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Investment Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg bg-slate-50 p-4">
              <div className="text-sm text-slate-500">
                Total Investment Needed
              </div>
              <div className="text-xl font-bold text-slate-700">
                ${summary.totalInvestmentNeeded.toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {dailyCost.toLocaleString()} × {simulationDays} days
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-4">
              <div className="text-sm text-slate-500">
                Initial Fund Coverage
              </div>
              <div className="text-xl font-bold text-blue-600">
                {Math.min(
                  100,
                  (initialFund / summary.totalInvestmentNeeded) * 100,
                ).toFixed(2)}
                %
              </div>
              <div className="mt-1 text-xs text-slate-400">
                Covers {Math.floor(initialFund / dailyCost)} days
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-4">
              <div className="text-sm text-slate-500">
                Additional Fund Needed
              </div>
              <div
                className={`text-xl font-bold ${
                  summary.additionalFundNeeded > 0
                    ? 'text-amber-600'
                    : 'text-green-600'
                }`}
              >
                ${summary.additionalFundNeeded.toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {summary.additionalFundNeeded > 0
                  ? 'Needs funding'
                  : 'Fully covered'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cash Flow Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Fixed Expansion Cash Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={simulationData}>
              <defs>
                <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `D${value}`}
              />
              <YAxis
                yAxisId="left"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${(value / 1000).toFixed(2)}K`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <p className="font-semibold">Day {data.day}</p>
                        <p className="text-sm text-red-500">
                          Daily Cost: ${data.todayCost?.toLocaleString()}
                        </p>
                        <p className="text-sm text-green-600">
                          Daily Revenue: ${data.todayRevenue?.toLocaleString()}
                        </p>
                        <p className="text-sm text-blue-600">
                          Net Cash Flow: $
                          {data.cumulativeNetCashFlow?.toLocaleString()}
                        </p>
                        <p className="text-sm text-purple-600">
                          Withdrawable: ${data.withdrawable?.toLocaleString()}
                        </p>
                        <p className="text-sm text-amber-600">
                          ROI: {data.roi}%
                        </p>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Legend />
              <ReferenceLine
                yAxisId="left"
                y={0}
                stroke="#64748b"
                strokeDasharray="3 3"
              />
              <ReferenceLine
                yAxisId="right"
                y={targetRoi}
                stroke="#22c55e"
                strokeDasharray="5 5"
                label={{
                  value: `Target ${targetRoi}%`,
                  position: 'right',
                  fontSize: 12,
                  fill: '#22c55e',
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="cumulativeNetCashFlow"
                name="Net Cash Flow"
                stroke="#3b82f6"
                fill="url(#profitGradient)"
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="withdrawable"
                name="Withdrawable"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roi"
                name="ROI"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Cash Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Cash Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
              data={simulationData.slice(0, Math.min(90, simulationDays))}
            >
              <XAxis
                dataKey="day"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `D${value}`}
              />
              <YAxis
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <p className="font-semibold">Day {data.day}</p>
                        <p className="text-sm text-red-500">
                          Cost: ${data.todayCost?.toLocaleString()}
                        </p>
                        <p className="text-sm text-green-600">
                          Revenue: ${data.todayRevenue?.toLocaleString()}
                        </p>
                        <p
                          className={`text-sm ${
                            data.netCashFlow >= 0
                              ? 'text-green-600'
                              : 'text-red-500'
                          }`}
                        >
                          Net: ${data.netCashFlow?.toLocaleString()}
                        </p>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Legend />
              <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
              <Bar dataKey="todayCost" name="Daily Cost" fill="#ef4444" />
              <Bar dataKey="todayRevenue" name="Daily Revenue" fill="#22c55e" />
              <Line
                type="monotone"
                dataKey="netCashFlow"
                name="Net Flow"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Milestones Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Key Milestones</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead className="text-right">Total Cost</TableHead>
                <TableHead className="text-right">Total Revenue</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Withdrawable</TableHead>
                <TableHead className="text-right">ROI</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {milestones.map((item) => (
                <TableRow key={item.day}>
                  <TableCell className="font-medium">Day {item.day}</TableCell>
                  <TableCell className="text-right text-red-500">
                    ${item.totalCost?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-green-600">
                    ${item.totalRevenue?.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`text-right ${
                      item.profit >= 0 ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    ${item.profit?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-purple-600">
                    ${item.withdrawable?.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`text-right font-semibold ${
                      Number(item.roi) >= targetRoi
                        ? 'text-green-600'
                        : 'text-amber-600'
                    }`}
                  >
                    {item.roi}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default FixedExpansion
